<template>
    <div class="mb-4">
        <div class="flex items-center gap-1">
            <h3 class="mb-1 text-sm font-semibold text-gray-900">Amenities</h3>
            <ToolTip
                v-if="isSharedViewOnly && viewOnlyTooltip"
                :title="viewOnlyTooltip.title"
                :description="viewOnlyTooltip.description"
            />
        </div>
        <div
            class="flex flex-wrap items-center gap-1.5 transition-opacity"
            :class="{ 'opacity-60': saving }"
            :aria-busy="saving ? 'true' : 'false'"
        >
            <span
                v-for="amenity in amenityChips"
                :key="amenity.key"
                class="inline-flex items-center gap-1 rounded-full bg-gray-100 py-0.5 text-xs font-medium text-gray-800"
                :class="canEdit ? 'pl-2 pr-1' : 'px-2'"
            >
                {{ amenity.name }}
                <button
                    v-if="canEdit"
                    type="button"
                    class="rounded-full p-0.5 text-gray-500 hover:bg-white hover:text-gray-800 disabled:opacity-50"
                    :aria-label="`Remove ${amenity.name}`"
                    :disabled="saving"
                    @click="removeAmenity(amenity)"
                >
                    <font-awesome-icon :icon="faXmark" class="h-3 w-3" aria-hidden="true" />
                </button>
            </span>
            <button
                v-if="!property.deleted_at && !adding"
                type="button"
                data-test="add-more-amenities"
                class="inline-flex items-center rounded-full border border-dashed border-gray-300 px-2 py-0.5 text-xs font-medium text-blue-600 hover:border-blue-400 hover:text-blue-800 disabled:pointer-events-none disabled:opacity-50"
                :disabled="isSharedViewOnly"
                @click="startAdding"
            >
                + Add More
            </button>
        </div>

        <div v-if="adding" class="relative mt-2 max-w-sm">
            <div class="flex gap-2">
                <input
                    ref="newAmenityInput"
                    v-model="newAmenityName"
                    type="text"
                    role="combobox"
                    aria-label="Add an amenity"
                    aria-autocomplete="list"
                    aria-controls="amenity-suggestions"
                    :aria-expanded="suggestions.length > 0 ? 'true' : 'false'"
                    :maxlength="maxNameLength"
                    placeholder="Type an amenity, e.g. Pool"
                    class="min-w-0 flex-1 rounded-md border-gray-300 py-1 text-sm focus:border-rent-risk-blue-400 focus:ring-rent-risk-blue-400"
                    @input="onNameInput"
                    @keydown.enter.prevent="submitNewAmenity"
                    @keydown.escape.prevent="cancelAdding"
                    @keydown.down.prevent="moveHighlight(1)"
                    @keydown.up.prevent="moveHighlight(-1)"
                />
                <button
                    type="button"
                    class="rounded-md bg-rent-risk-blue-400 px-3 py-1 text-sm font-semibold text-white disabled:opacity-50"
                    :disabled="saving"
                    @click="submitNewAmenity"
                >
                    Add
                </button>
                <button
                    type="button"
                    class="rounded-md border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-700"
                    @click="cancelAdding"
                >
                    Cancel
                </button>
            </div>
            <ul
                v-if="suggestions.length"
                id="amenity-suggestions"
                role="listbox"
                class="absolute z-20 mt-1 w-full rounded-md border border-gray-200 bg-white py-1 shadow-lg"
            >
                <li
                    v-for="(option, index) in suggestions"
                    :key="option.id"
                    role="option"
                    :aria-selected="index === highlightedIndex ? 'true' : 'false'"
                >
                    <button
                        type="button"
                        class="block w-full px-3 py-1.5 text-left text-sm text-gray-700 hover:bg-gray-100"
                        :class="index === highlightedIndex ? 'bg-gray-100' : ''"
                        @mousedown.prevent="pickSuggestion(option)"
                    >
                        {{ option.name }}
                    </button>
                </li>
            </ul>
            <p v-if="addError || serverError" class="mt-1 text-xs text-red-600">
                {{ addError || serverError }}
            </p>
        </div>
        <p v-else-if="serverError" class="mt-1 text-xs text-red-600">{{ serverError }}</p>
    </div>
</template>

<script setup>
import { computed, nextTick, ref } from 'vue'
import axios from 'axios'
import { router } from '@inertiajs/vue3'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faXmark } from '@fortawesome/pro-solid-svg-icons'
import ToolTip from '@/Shared/ToolTip.vue'
import {
    MAX_CUSTOM_AMENITY_NAME_LENGTH,
    cleanAmenityName,
    comparableAmenityName,
    customAmenityError,
} from './propertyAmenities'

const route =
    (typeof window !== 'undefined' && window.route) ||
    ((..._args) => '#')

// 86bc2cgxd: typed amenities that match nothing in the checklist are saved as
// custom amenities in this section.
const CUSTOM_CATEGORY = 'optional'
const MAX_SUGGESTIONS = 6

const props = defineProps({
    property: {
        type: Object,
        required: true,
    },
    amenityOptions: {
        type: Object,
        default: () => ({}),
    },
    propertyAccess: {
        type: String,
        default: 'owner',
    },
    viewOnlyTooltip: {
        type: Object,
        default: null,
    },
})

const isSharedViewOnly = computed(() => props.propertyAccess === 'shared_view')
const canEdit = computed(() => !props.property.deleted_at && !isSharedViewOnly.value)
const maxNameLength = MAX_CUSTOM_AMENITY_NAME_LENGTH

const saving = ref(false)
const serverErrors = ref({})

const adding = ref(false)
const newAmenityName = ref('')
const newAmenityInput = ref(null)
const addError = ref('')
const highlightedIndex = ref(-1)

const amenityGroups = computed(() => props.amenityOptions || {})
const checklistOptions = computed(() => Object.values(amenityGroups.value).flat())

const savedAmenityIds = () => (props.property.amenities || []).map((amenity) => amenity.id)

const savedCustomAmenities = () => (props.property.custom_amenities || []).map((amenity) => ({
    category: amenity.category,
    name: amenity.name,
}))

// Checklist amenities first, then custom ones — every one is shown (no "Show All").
const amenityChips = computed(() => [
    ...(props.property.amenities || []).map((amenity) => ({
        key: `amenity-${amenity.id}`,
        name: amenity.name,
        amenityId: amenity.id,
    })),
    ...(props.property.custom_amenities || []).map((amenity) => ({
        key: `custom-${amenity.id}`,
        name: amenity.name,
        custom: { category: amenity.category, name: amenity.name },
    })),
])

// Checklist amenities not on the property yet whose name contains what was typed.
const suggestions = computed(() => {
    const typed = comparableAmenityName(newAmenityName.value)
    if (!typed) {
        return []
    }
    const selected = savedAmenityIds()

    return checklistOptions.value
        .filter((option) => !selected.includes(option.id) && comparableAmenityName(option.name).includes(typed))
        .slice(0, MAX_SUGGESTIONS)
})

const serverError = computed(() => {
    const first = Object.values(serverErrors.value)[0]

    return (Array.isArray(first) ? first[0] : first) || ''
})

const SAVE_FAILED_MESSAGE = 'Could not save amenities. Please try again.'

// 86bc2cgxd: saved over JSON so a chip change does not re-render the whole
// property page (Stripe, RocketLawyer, tab data). replaceProp updates the
// page's `property` prop, and its history entry, with the saved lists — only
// while that page still shows this property (the landlord may have moved on).
const save = async (selectedAmenities, customAmenities, onSuccess = () => {}) => {
    const propertyId = props.property.id
    saving.value = true
    serverErrors.value = {}

    try {
        const { data } = await axios.patch(
            route('properties.update-amenities', propertyId),
            { selectedAmenities, customAmenities },
            { headers: { Accept: 'application/json' } },
        )

        // A redirect (e.g. a lost session) is followed to an HTML page, not our payload.
        if (!Array.isArray(data?.amenities) || !Array.isArray(data?.custom_amenities)) {
            serverErrors.value = { general: SAVE_FAILED_MESSAGE }

            return
        }

        router.replaceProp('property', (property) => (property?.id === propertyId
            ? { ...property, amenities: data.amenities, custom_amenities: data.custom_amenities }
            : property))
        onSuccess()
    } catch (error) {
        serverErrors.value = error.response?.data?.errors
            || { general: error.response?.data?.message || SAVE_FAILED_MESSAGE }
    } finally {
        saving.value = false
    }
}

const resetAdding = () => {
    newAmenityName.value = ''
    addError.value = ''
    highlightedIndex.value = -1
}

const startAdding = async () => {
    if (!canEdit.value) {
        return
    }
    resetAdding()
    serverErrors.value = {}
    adding.value = true
    await nextTick()
    newAmenityInput.value?.focus()
}

const cancelAdding = () => {
    adding.value = false
    resetAdding()
}

const onNameInput = () => {
    addError.value = ''
    highlightedIndex.value = -1
}

const moveHighlight = (step) => {
    const count = suggestions.value.length
    if (!count) {
        return
    }
    highlightedIndex.value = (highlightedIndex.value + step + count) % count
}

const pickSuggestion = (option) => {
    if (saving.value) {
        return
    }
    save([...savedAmenityIds(), option.id], savedCustomAmenities(), cancelAdding)
}

const submitNewAmenity = () => {
    if (saving.value) {
        return
    }

    const highlighted = suggestions.value[highlightedIndex.value]
    if (highlighted) {
        pickSuggestion(highlighted)

        return
    }

    const name = cleanAmenityName(newAmenityName.value)
    const comparable = comparableAmenityName(name)

    // A name that is already in the checklist ticks that amenity rather than
    // creating a look-alike custom one.
    const checklistMatch = checklistOptions.value.find((option) => comparableAmenityName(option.name) === comparable)
    if (checklistMatch) {
        if (savedAmenityIds().includes(checklistMatch.id)) {
            addError.value = `"${checklistMatch.name}" is already added.`

            return
        }
        pickSuggestion(checklistMatch)

        return
    }

    const customMatch = savedCustomAmenities().find((amenity) => comparableAmenityName(amenity.name) === comparable)
    if (name && customMatch) {
        addError.value = `"${customMatch.name}" is already added.`

        return
    }

    const error = customAmenityError(name, CUSTOM_CATEGORY, savedCustomAmenities(), amenityGroups.value)
    if (error) {
        addError.value = error

        return
    }

    save(savedAmenityIds(), [...savedCustomAmenities(), { category: CUSTOM_CATEGORY, name }], cancelAdding)
}

const removeAmenity = (chip) => {
    if (!canEdit.value || saving.value) {
        return
    }

    if (chip.amenityId !== undefined) {
        save(savedAmenityIds().filter((id) => id !== chip.amenityId), savedCustomAmenities())

        return
    }

    save(
        savedAmenityIds(),
        savedCustomAmenities().filter((amenity) => !(amenity.category === chip.custom.category && amenity.name === chip.custom.name)),
    )
}
</script>
